import Vue from "vue";
import lodash from "lodash";
import constants from "@/assets/constants";

export default {
  namespaced: true,
  state: {
    initialized: false,
    publicQuizzes: null,
    hostedQuizzes: null,
    calendarQuizzes: null
  },
  mutations: {
    /**
     * Internal use, prevent double-setup ws listeners
     * @param {*} state
     * @param {*} value
     */
    setInitialized(state, value) {
      state.initialized = value;
    },
    /**
     * Removes a quiz from the calendar
     * @param {*} state
     * @param {*} quizID
     */
    removeFromCalendar(state, quizID) {
      Vue.delete(state.calendarQuizzes, quizID);
    },
    /**
     * Removes all public quizzes
     * @param {*} state
     */
    clearPublicQuizzes(state) {
      state.publicQuizzes = {};
    },
    /**
     * Adds a quiz to the calendar if it fits the criteria for public viewing
     * @param {*} state
     * @param {*} quiz
     */
    addPublicQuiz(state, quiz) {
      if (!quiz) {
        return;
      }

      if (
        quiz.status !== undefined &&
        (quiz.status == constants["QUIZ_STATUS_NEW"] ||
          quiz.status >= constants["QUIZ_STATUS_RUNNING_CLOSED"])
      ) {
        return;
      }

      if (quiz.public !== undefined && quiz.public == 0) {
        return;
      }

      Vue.set(state.publicQuizzes, quiz.id, quiz);
    },
    /**
     * Removes a quiz from the public quiz list
     * @param {*} state
     * @param {*} quizID
     */
    removePublicQuiz(state, quizID) {
      Vue.delete(state.publicQuizzes, quizID);
    },
    /**
     * Clears all quizzes from the hosted quiz list
     * @param {*} state
     */
    clearHostedQuizzes(state) {
      state.hostedQuizzes = {};
    },
    /**
     * Adds a quiz to the list of the user's hosted quizzes
     * @param {*} state
     * @param {*} quiz
     */
    addHostedQuiz(state, quiz) {
      Vue.set(state.hostedQuizzes, quiz.id, quiz);
    },
    /**
     * Clears your calendar. Badumm tss!
     * @param {*} state
     */
    clearCalendarQuizzes(state) {
      state.calendarQuizzes = {};
    },
    /**
     * Adds a quiz to the calendar
     * @param {*} state
     * @param {*} quiz
     */
    addCalendarQuiz(state, quiz) {
      Vue.set(state.calendarQuizzes, quiz.id, quiz);
    },
    /**
     * Changes the link to a website in the calendar
     * @param {*} state
     * @param {Object with quizID and website parameters} data
     */
    updateCalendarWebsite(state, data) {
      Vue.set(
        state.calendarQuizzes[data.quizID],
        "link_to_website",
        data.website
      );
    }
  },
  actions: {
    /**
     * Runs only once (when not initialized),
     * sets up all the necessary topquiz handlers to manage this store
     * @param {*} me
     * @param {*} vm
     */
    storeCreated(me, vm) {
      if (me.state.initialized === true) {
        return;
      }

      me.commit("setInitialized", true);

      vm.$topquiz.on(this, "getQuizzes_success", params => {
        me.commit("clearPublicQuizzes");

        var quizList = params[0];
        quizList.forEach(quiz => {
          me.commit("addPublicQuiz", quiz);
        });
      });

      vm.$topquiz.on(this, "quizStatusChanged", params => {
        var quizID = params[0];
        var status = params[1];
        var quiz = params[2];

        if (me.state.publicQuizzes !== null) {
          if (
            status == constants["QUIZ_STATUS_NEW"] ||
            status >= constants["QUIZ_STATUS_RUNNING_CLOSED"]
          ) {
            me.commit("removePublicQuiz", quizID);
          } else {
            me.commit("addPublicQuiz", quiz);
          }
        }
      });

      vm.$topquiz.on(this, "quizVisibilityChanged", params => {
        var quizID = params[0];
        var visible = params[1];
        var quiz = params[2];

        if (me.state.publicQuizzes !== null) {
          if (!visible) {
            me.commit("removePublicQuiz", quizID);
          } else {
            me.commit("addPublicQuiz", quiz);
          }
        }
      });

      vm.$topquiz.on(this, "getHostedQuizzes_success", params => {
        me.commit("clearHostedQuizzes");

        var quizList = params[0];
        quizList.forEach(quiz => {
          me.commit("addHostedQuiz", quiz);
        });
      });

      vm.$topquiz.on(this, "calendar_nextQuizzes_success", params => {
        me.commit("clearCalendarQuizzes");

        var quizList = params[0];
        quizList.forEach(quiz => {
          me.commit("addCalendarQuiz", quiz);
        });
      });

      vm.$topquiz.on(this, "quizCalendarVisibilityChanged", params => {
        var quizID = params[0];
        var inCalendar = params[1];

        if (me.state.calendarQuizzes !== null) {
          var local = me.state.calendarQuizzes[quizID];
          if (local && !inCalendar) {
            me.commit("removeFromCalendar", quizID);
          } else {
            me.dispatch("loadCalendar", vm);
          }
        }
      });

      vm.$topquiz.on(this, "quizWebsiteChanged", params => {
        var quizID = params[0];
        var website = params[1];

        if (me.state.calendarQuizzes !== null) {
          var local = me.state.calendarQuizzes[quizID];
          if (local) {
            me.commit("updateCalendarWebsite", { quizID, website });
          }
        }
      });
    },
    /**
     * Loads all public quizzes from the database, replacing the old ones
     * @param {*} me
     * @param {*} vm
     */
    loadPublicQuizzes(me, vm) {
      me.dispatch("storeCreated", vm);
      vm.$topquiz.sendCommand("getQuizzes", vm.$topquiz.SessionKey);
    },
    /**
     * Loads all the user's hosted quizzes from the database, replacing the old ones
     * @param {*} me
     * @param {*} vm
     */
    loadHostedQuizzes(me, vm) {
      me.dispatch("storeCreated", vm);
      vm.$topquiz.sendCommand("getHostedQuizzes", vm.$topquiz.SessionKey);
    },
    /**
     * Loads all public calendar items from the database, replacing the old ones
     * @param {*} me
     * @param {*} vm
     */
    loadCalendar(me, vm) {
      me.dispatch("storeCreated", vm);
      vm.$topquiz.sendCommand("calendar_nextQuizzes");
    }
  },
  getters: {
    sortedHostQuizzes(state) {
      if (state.hostedQuizzes === null) {
        return null;
      }

      var filteredHistory = lodash.filter(
        state.hostedQuizzes,
        q => q.status < constants["QUIZ_STATUS_FINALIZED"]
      );
      var sortedHistory = lodash.sortBy(filteredHistory, [
        quiz => -quiz.event_time
      ]);

      return sortedHistory;
    },
    sortedHostQuizHistory(state) {
      if (state.hostedQuizzes === null) {
        return null;
      }

      var sortedHistory = lodash.sortBy(state.hostedQuizzes, [
        quiz => -quiz.event_time
      ]);
      return sortedHistory;
    },
    sortedPublicQuizzes(state) {
      if (state.publicQuizzes === null) {
        return null;
      }

      var sortedHistory = lodash.sortBy(state.publicQuizzes, [
        quiz => -quiz.event_time
      ]);
      return sortedHistory;
    },
    sortedCalendar(state) {
      if (state.calendarQuizzes === null) {
        return null;
      }

      var sortedHistory = lodash.sortBy(state.calendarQuizzes, [
        quiz => -quiz.event_time
      ]);
      return sortedHistory;
    }
  }
};
